import { useMemo, useRef, useState } from 'react'
import {
  GATE_HEIGHT,
  GATE_WIDTH,
  NODE_HEIGHT,
  NODE_WIDTH,
  chainOf,
  layoutQuestGraph,
  type GraphNode,
} from '../../domain/layout'
import type { CatalogIndex } from '../../catalog/types'
import type { Quest } from '../../domain/types'

/* Deep fills that sit on the near-black ground, lit strokes to separate them.
   MAIN takes the brand gold since it is the storyline spine. */
const TAG_FILL: Record<string, string> = {
  TUTORIAL: '#0e2740',
  MAIN: '#3a2a00',
  LOCATION: '#0d2f1c',
  TOOL: '#2d0a4d',
  SIDE: '#1a1226',
}
const TAG_STROKE: Record<string, string> = {
  TUTORIAL: '#4aa3e0',
  MAIN: '#ffde00',
  LOCATION: '#5ee08a',
  TOOL: '#a13ceb',
  SIDE: '#6b5a80',
}

const PADDING = 40

export function QuestGraph({
  quests,
  catalog,
  selectedId,
  onSelect,
}: {
  quests: Quest[]
  catalog: CatalogIndex
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const layout = useMemo(() => layoutQuestGraph(quests, catalog), [quests, catalog])
  const [view, setView] = useState({ x: PADDING, y: PADDING, scale: 1 })
  const [hovered, setHovered] = useState<string | null>(null)
  // Collapsed by default: the shelf holds quests that are deliberately out of play,
  // so it should not eat height from the graph until it is asked for.
  const [shelfOpen, setShelfOpen] = useState(false)
  const dragRef = useRef<{ x: number; y: number; vx: number; vy: number } | null>(null)
  const svgRef = useRef<SVGSVGElement>(null)

  const focus = hovered ?? selectedId
  const chain = useMemo(
    () => (focus ? chainOf(focus, layout.edges) : null),
    [focus, layout.edges],
  )

  const inChain = (id: string) =>
    !chain || id === focus || chain.ancestors.has(id) || chain.descendants.has(id)

  const cyclic = useMemo(() => new Set(layout.cyclicIds), [layout.cyclicIds])

  const nodeById = useMemo(
    () => new Map(layout.nodes.map((node) => [node.id, node])),
    [layout.nodes],
  )

  function onWheel(event: React.WheelEvent) {
    event.preventDefault()
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect) return
    const px = event.clientX - rect.left
    const py = event.clientY - rect.top
    const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12
    setView((current) => {
      const scale = Math.min(2.5, Math.max(0.2, current.scale * factor))
      // Keep the point under the cursor fixed while zooming.
      const ratio = scale / current.scale
      return { scale, x: px - (px - current.x) * ratio, y: py - (py - current.y) * ratio }
    })
  }

  function onPointerDown(event: React.PointerEvent) {
    if (event.button !== 0) return
    dragRef.current = { x: event.clientX, y: event.clientY, vx: view.x, vy: view.y }
    ;(event.target as Element).setPointerCapture?.(event.pointerId)
  }

  function onPointerMove(event: React.PointerEvent) {
    const drag = dragRef.current
    if (!drag) return
    setView((current) => ({
      ...current,
      x: drag.vx + (event.clientX - drag.x),
      y: drag.vy + (event.clientY - drag.y),
    }))
  }

  function endDrag() {
    dragRef.current = null
  }

  function fit() {
    const rect = svgRef.current?.getBoundingClientRect()
    if (!rect) return
    const scale = Math.min(
      1,
      (rect.width - PADDING * 2) / Math.max(1, layout.width),
      (rect.height - PADDING * 2) / Math.max(1, layout.height),
    )
    setView({ x: PADDING, y: PADDING, scale })
  }

  return (
    <div className="relative flex min-w-0 flex-1 flex-col bg-ink-lift">
      <div className="flex items-center gap-2 border-b border-edge px-4 py-2">
        <span className="text-sm font-semibold text-gold">Quest chains</span>
        <span className="text-xs text-dim">
          {layout.nodes.filter((n) => n.kind === 'quest').length} quests ·{' '}
          {layout.nodes.filter((n) => n.kind === 'gate').length} gates ·{' '}
          {layout.edges.length} links
          {layout.bank.length > 0 && ` · ${layout.bank.length} WIP`}
          {layout.orphaned.length > 0 && ` · ${layout.orphaned.length} orphaned`}
        </span>
        <div className="ml-auto flex items-center gap-1">
          {Object.keys(TAG_FILL).map((tag) => (
            <span key={tag} className="flex items-center gap-1 text-[10px] text-dim">
              <span
                className="inline-block h-2.5 w-2.5 rounded-sm"
                style={{ background: TAG_FILL[tag], border: `1px solid ${TAG_STROKE[tag]}` }}
              />
              {tag}
            </span>
          ))}
          <button
            type="button"
            onClick={fit}
            className="ml-2 rounded border border-edge px-2 py-0.5 text-xs hover:bg-ink-high"
          >
            Fit
          </button>
        </div>
      </div>

      <svg
        ref={svgRef}
        className="min-h-0 flex-1 cursor-grab active:cursor-grabbing"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        <defs>
          <marker
            id="arrow"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#8a76a3" />
          </marker>
          <marker
            id="arrow-broken"
            viewBox="0 0 10 10"
            refX="9"
            refY="5"
            markerWidth="6"
            markerHeight="6"
            orient="auto-start-reverse"
          >
            <path d="M 0 0 L 10 5 L 0 10 z" fill="#ff6b6b" />
          </marker>
        </defs>

        <g transform={`translate(${view.x} ${view.y}) scale(${view.scale})`}>
          {layout.edges.map((edge, index) => {
            const from = nodeById.get(edge.from)
            const to = nodeById.get(edge.to)
            if (!from || !to) return null
            const lit = !chain || (inChain(edge.from) && inChain(edge.to))
            // A self-loop drawn as a normal curve runs backwards behind its own node
            // and becomes invisible — exactly the case that most needs to be seen.
            if (edge.from === edge.to) {
              return (
                <path
                  key={index}
                  d={selfLoopPath(from)}
                  fill="none"
                  stroke="#ff6b6b"
                  strokeWidth={2}
                  strokeDasharray="5 3"
                  markerEnd="url(#arrow-broken)"
                />
              )
            }
            const mid = edgeMidpoint(from, to)
            return (
              <g key={index} opacity={lit ? 1 : 0.45}>
                <path
                  d={edgePath(from, to)}
                  fill="none"
                  stroke={edge.broken ? '#ff6b6b' : lit ? '#8a76a3' : '#2a1c3d'}
                  strokeWidth={edge.broken ? 2 : lit ? 1.5 : 1}
                  strokeDasharray={edge.broken ? '5 3' : undefined}
                  markerEnd={`url(#${edge.broken ? 'arrow-broken' : 'arrow'})`}
                />
                {edge.label && (
                  <>
                    {/* Punch the ground through the path so the count stays readable. */}
                    <rect
                      x={mid.x - 13}
                      y={mid.y - 7}
                      width={26}
                      height={14}
                      rx={3}
                      fill="#0a0510"
                    />
                    <text
                      x={mid.x}
                      y={mid.y + 3.5}
                      fontSize={10}
                      textAnchor="middle"
                      fill={lit ? '#b9a7cc' : '#6b5a80'}
                      fontFamily="ui-monospace, monospace"
                    >
                      {edge.label}
                    </text>
                  </>
                )}
              </g>
            )
          })}

          {layout.nodes.map((node) => (
            <NodeBox
              key={node.id}
              node={node}
              selected={node.id === selectedId}
              dimmed={!inChain(node.id)}
              broken={cyclic.has(node.id)}
              onSelect={onSelect}
              onHover={setHovered}
            />
          ))}
        </g>
      </svg>

      {(layout.bank.length > 0 || layout.orphaned.length > 0) && (
        <div className="border-t border-edge bg-ink-deep">
          <button
            type="button"
            onClick={() => setShelfOpen((open) => !open)}
            aria-expanded={shelfOpen}
            className="flex w-full items-center gap-2 px-4 py-1.5 text-left text-xs text-dim hover:bg-ink-high"
          >
            <span className="text-[10px] text-warn">{shelfOpen ? '▼' : '▶'}</span>
            <span className="font-semibold text-warn">
              {layout.bank.length > 0 && `${layout.bank.length} work in progress`}
              {layout.bank.length > 0 && layout.orphaned.length > 0 && ' · '}
              {layout.orphaned.length > 0 && `${layout.orphaned.length} orphaned`}
            </span>
            <span className="text-dim">
              {shelfOpen ? 'hide' : 'not drawn in the graph — show'}
            </span>
          </button>
          {shelfOpen && (
            <div className="max-h-40 overflow-y-auto px-4 pb-2">
              <Tray
                title="Work in progress — kept in the data, never offered to the player"
                nodes={layout.bank}
                selectedId={selectedId}
                onSelect={onSelect}
                warn
              />
              <Tray
                title="Orphaned — no prereqs at all, and nothing depends on them"
                nodes={layout.orphaned}
                selectedId={selectedId}
                onSelect={onSelect}
                warn
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function Tray({
  title,
  nodes,
  selectedId,
  onSelect,
  warn = false,
}: {
  title: string
  nodes: GraphNode[]
  selectedId: string | null
  onSelect: (id: string) => void
  warn?: boolean
}) {
  if (nodes.length === 0) return null
  return (
    <div className="mb-2 last:mb-0">
      <div className={`mb-1 text-xs font-semibold ${warn ? 'text-warn' : 'text-parchment'}`}>
        {title}
      </div>
      <div className="flex flex-wrap gap-1">
        {nodes.map((node) => (
          <button
            key={node.id}
            type="button"
            onClick={() => onSelect(node.id)}
            className={`rounded border px-2 py-0.5 text-xs ${
              node.id === selectedId
                ? 'border-gold bg-gold text-ink'
                : warn
                  ? 'border-warn-edge bg-warn-bg text-warn hover:border-warn'
                  : 'border-edge bg-ink-lift text-parchment hover:border-gold-shade'
            }`}
            style={
              node.id === selectedId || warn
                ? undefined
                : { borderLeft: `3px solid ${TAG_STROKE[node.tag] ?? '#6b5a80'}` }
            }
            title={`${node.id} · gate ${node.gatingLevel}`}
          >
            {node.label}
          </button>
        ))}
      </div>
    </div>
  )
}

function NodeBox({
  node,
  selected,
  dimmed,
  broken,
  onSelect,
  onHover,
}: {
  node: GraphNode
  selected: boolean
  dimmed: boolean
  broken: boolean
  onSelect: (id: string) => void
  onHover: (id: string | null) => void
}) {
  return (
    <g
      transform={`translate(${node.x} ${node.y})`}
      className="cursor-pointer"
      opacity={dimmed ? 0.3 : 1}
      onClick={() => onSelect(node.id)}
      onMouseEnter={() => onHover(node.id)}
      onMouseLeave={() => onHover(null)}
    >
      {node.kind === 'gate' && (
        <g transform={`translate(${(NODE_WIDTH - GATE_WIDTH) / 2} ${(NODE_HEIGHT - GATE_HEIGHT) / 2})`}>
          <title>{`Condition: ${node.label}`}</title>
          {/* Pointed ends read as a gate rather than a card, and unlike a rotated
              square this still holds a location name at legible size. */}
          <path
            d={`M 10 0 L ${GATE_WIDTH - 10} 0 L ${GATE_WIDTH} ${GATE_HEIGHT / 2} L ${GATE_WIDTH - 10} ${GATE_HEIGHT} L 10 ${GATE_HEIGHT} L 0 ${GATE_HEIGHT / 2} Z`}
            fill="#1d1430"
            stroke="#8a76a3"
            strokeWidth={1}
          />
          <text
            x={GATE_WIDTH / 2}
            y={GATE_HEIGHT / 2 + 3.5}
            fontSize={10}
            textAnchor="middle"
            fill="#b9a7cc"
          >
            {truncate(node.label, 20)}
          </text>
        </g>
      )}
      {node.kind === 'quest' && (
        <>
      <title>
        {broken
          ? `${node.id}\nUnreachable — caught in a prereq cycle`
          : `${node.id}\n${node.tag} · gate ${node.gatingLevel}${
              node.disabled ? '\nWork in progress — never offered to the player' : ''
            }`}
      </title>
      <rect
        width={NODE_WIDTH}
        height={NODE_HEIGHT}
        rx={6}
        fill={broken ? '#3d0f18' : (TAG_FILL[node.tag] ?? '#1a1226')}
        stroke={broken ? '#ff6b6b' : selected ? '#ffde00' : (TAG_STROKE[node.tag] ?? '#6b5a80')}
        strokeWidth={broken ? 2 : selected ? 2.5 : 1}
        /* A WIP quest is real data the player never sees, so it reads as an
           outline rather than a solid card. */
        strokeDasharray={node.disabled ? '4 3' : undefined}
        fillOpacity={node.disabled ? 0.35 : 1}
      />
      {node.disabled && (
        <text x={10} y={NODE_HEIGHT - 4} fontSize={8} fill="#ffb020" letterSpacing={0.5}>
          WIP
        </text>
      )}
      {/* Drawn rather than a glyph — this marker must not depend on font coverage. */}
      {broken && (
        <g transform={`translate(${NODE_WIDTH - 22} 8)`}>
          <path d="M 6 0 L 12 11 L 0 11 Z" fill="#ff6b6b" />
          <rect x={5.2} y={3.5} width={1.6} height={4} fill="#0a0510" />
          <rect x={5.2} y={8.4} width={1.6} height={1.6} fill="#0a0510" />
        </g>
      )}
      <text x={10} y={19} fontSize={12} fontWeight={600} fill="#fff7c4">
        {truncate(node.label, 24)}
      </text>
      <text x={10} y={34} fontSize={10} fill="#b9a7cc" fontFamily="ui-monospace, monospace">
        {truncate(node.id, 18)}
      </text>
      <text
        x={NODE_WIDTH - 10}
        y={34}
        fontSize={10}
        fill="#b9a7cc"
        textAnchor="end"
        fontFamily="ui-monospace, monospace"
      >
        {node.gatingLevel}
      </text>
        </>
      )}
    </g>
  )
}

/** Gates sit centred inside the same grid cell, drawn smaller than a quest card. */
function boxOf(node: GraphNode): { x: number; y: number; w: number; h: number } {
  if (node.kind === 'gate') {
    return {
      x: node.x + (NODE_WIDTH - GATE_WIDTH) / 2,
      y: node.y + (NODE_HEIGHT - GATE_HEIGHT) / 2,
      w: GATE_WIDTH,
      h: GATE_HEIGHT,
    }
  }
  return { x: node.x, y: node.y, w: NODE_WIDTH, h: NODE_HEIGHT }
}

/** For this curve's control points the t=0.5 point reduces to the endpoint average. */
function edgeMidpoint(from: GraphNode, to: GraphNode): { x: number; y: number } {
  const a = boxOf(from)
  const b = boxOf(to)
  return { x: (a.x + a.w + b.x) / 2, y: (a.y + a.h / 2 + b.y + b.h / 2) / 2 }
}

/** Cubic bezier leaving the parent's right edge and arriving at the child's left. */
function edgePath(from: GraphNode, to: GraphNode): string {
  const a = boxOf(from)
  const b = boxOf(to)
  const x1 = a.x + a.w
  const y1 = a.y + a.h / 2
  const x2 = b.x
  const y2 = b.y + b.h / 2
  const dx = Math.max(40, (x2 - x1) / 2)
  return `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`
}

/** A visible loop arcing over the top of the node, rather than behind it. */
function selfLoopPath(node: GraphNode): string {
  const left = node.x + NODE_WIDTH * 0.3
  const right = node.x + NODE_WIDTH * 0.7
  const top = node.y
  const peak = top - 26
  return `M ${left} ${top} C ${left} ${peak}, ${right} ${peak}, ${right} ${top}`
}

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text
}
